import API from '@/api/apiUrls'
import { useApi } from '@/api/useApi'
import { defineStore } from 'pinia'
import { useBaseStore } from '@haskoning/vue-basestore'
import { computed, ref, type Ref } from 'vue'
import { useAuthorizationStore } from '@/stores/authorization'
import type { ShipType } from '@/stores/type/ship.type'
import { useProjectStore } from '@/stores/project'
import { useSelectedItemMixin } from '@/stores/useSelectedItemMixin'

const { api } = useApi()
const baseURL = API.SHIP.SHIP.INDEX

export const useShipStore = defineStore('ship', () => {
  const authorizationStore = useAuthorizationStore()
  const inputFields: Ref<(keyof ShipType)[]> = ref([
    'name',
    'length_overall',
    'breadth',
    'main_engine_power',
    'main_engine_number',
    'main_engine_diameter',
    'main_engine_distance_between',
    'main_engine_distance_tip_keel',
    'main_engine_distance_ship_stern',
    'rudder',
    'main_engine_type',
    'transverse_thruster_power',
    'transverse_thruster_number',
    'transverse_thruster_diameter',
    'transverse_thruster_distance_between',
    'transverse_thruster_distance_tip_keel',
    'transverse_thruster_duct_length',
    'transverse_thruster_type'
  ])

  const canAddShip = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['add_ship']
    })
  })

  const canChangeShip = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['change_ship']
    })
  })

  const canDeleteShip = computed(() => {
    return authorizationStore.hasServicePermission({
      authSource: 'ProjectUser',
      allowed: ['delete_ship']
    })
  })

  const baseStore = useBaseStore({ service: baseURL, api })
  const selectedItemMixin = useSelectedItemMixin(baseStore)

  function resetAll() {
    baseStore.reset()
  }

  const bulkCreateShip = async (data: ShipType[]) => {
    const projectStore = useProjectStore()
    const projectId = projectStore.currentItem?.id
    if (projectId) {
      data.forEach((item: ShipType) => {
        item.project = projectId
      })
      await baseStore.bulkCreate(data)
    }
  }

  const createNewShip = (project: number) => {
    const newShip = {} as ShipType

    baseStore.fieldsList.value.forEach(({ parameter, children, default: defaultValue }) => {
      if (parameter === 'project') {
        newShip[parameter] = project
      } else if (children) {
        newShip[parameter] = Object.fromEntries(
          Object.entries(children).map(([subField, subFieldData]) => [
            subField,
            subFieldData.default
          ])
        )
      } else {
        newShip[parameter] = defaultValue
      }
    })

    return newShip
  }

  const getItemById = (id: number): ShipType | null => {
    return baseStore.items.value.find((item) => item.id === id) ?? null
  }

  return {
    ...baseStore,
    ...selectedItemMixin,
    inputFields,
    canAddShip,
    canChangeShip,
    canDeleteShip,
    bulkCreateShip,
    createNewShip,
    getItemById,
    resetAll
  }
})
